import { useState, useEffect } from 'react';
import { usersApiService } from '../services/usersApi';

// Module-level cache: Minecraft username → UUID
const usernameToUuidCache = new Map<string, string>();
const pendingLookups = new Map<string, Promise<string | null>>();

/**
 * Resolves a Minecraft username to a UUID, with caching.
 * Returns { uuid, loading } — uuid is null while loading or if lookup fails.
 */
export const usePlayerFromUsername = (username: string) => {
  const cached = usernameToUuidCache.get(username.toLowerCase());
  const [uuid, setUuid] = useState<string | null>(cached || null);
  const [loading, setLoading] = useState(!cached && !!username);

  useEffect(() => {
    if (!username) {
      setLoading(false);
      return;
    }

    const key = username.toLowerCase();

    if (usernameToUuidCache.has(key)) {
      setUuid(usernameToUuidCache.get(key)!);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Deduplicate in-flight requests for the same username
    let lookup = pendingLookups.get(key);
    if (!lookup) {
      lookup = usersApiService.getPlayerStatsByUsername(username)
        .then(response => {
          const resolvedUuid = response.player_stats.uuid;
          usernameToUuidCache.set(key, resolvedUuid);
          return resolvedUuid;
        })
        .catch(() => null)
        .finally(() => {
          pendingLookups.delete(key);
        });
      pendingLookups.set(key, lookup);
    }

    lookup.then(resolvedUuid => {
      setUuid(resolvedUuid);
      setLoading(false);
    });
  }, [username]);

  return { uuid, loading };
};

/**
 * Batch prefetch username→UUID mappings.
 * Fires all lookups concurrently and populates the cache.
 */
export const prefetchPlayerUuids = async (usernames: string[]): Promise<void> => {
  const uncached = usernames.filter(u => !usernameToUuidCache.has(u.toLowerCase()));
  if (uncached.length === 0) return;

  await Promise.allSettled(
    uncached.map(username =>
      usersApiService.getPlayerStatsByUsername(username)
        .then(response => {
          usernameToUuidCache.set(username.toLowerCase(), response.player_stats.uuid);
        })
        .catch(() => { /* silently skip failed lookups */ })
    )
  );

  // Notify listeners that cache has updated
  document.dispatchEvent(new Event('playerUuidsUpdated'));
};

/**
 * Synchronous cache read — returns UUID if previously resolved, else null.
 */
export const getCachedUuid = (username: string): string | null => {
  return usernameToUuidCache.get(username.toLowerCase()) || null;
};
